// ─────────────────────────────────────────────────────────────────────────────
// WP-16 — Piezas compartidas de Acceso: selector de idioma (7 idiomas), usado en el registro
// standalone de personal operativo (FrontlineRegisterView). Sin librerías nuevas — Font Awesome +
// CSS.
//
// La tab bar inferior móvil frontline (REQ-141, antes `FrontlineTabBar` en este mismo archivo) se
// unificó en Fase 4 (Task 14) con el nav inferior de Pulso: ver
// `src/components/layout/MobileNav.tsx`, que reutiliza los mismos destinos de
// `RECOGNITION_NAV_LINKS` y porta tal cual el menú "Más opciones" (documentos + idioma) que vivía
// aquí. "Panel Admin" quedó igual que antes: un link a `/app/admin`, no un flyout propio.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '@/i18n/languages';
import type { LegalDocType } from '@/api/recognition';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/common/Modal';
import { LegalPolicyModal } from './LegalPolicyModal';
import { asset } from '@/utils/basePath';

/** Selector de idioma entre los 7 soportados (es/en/de/pt/nl/fr/ro). Cambia i18n en vivo. */
export function LanguageSelector({ compact }: Readonly<{ compact?: boolean }>) {
  const { i18n } = useTranslation('recognition');
  const current = i18n.language?.split('-')[0] ?? 'es';
  return (
    <div className={`acc-lang ${compact ? 'acc-lang--compact' : ''}`}>
      <i className="fas fa-globe" aria-hidden="true" />
      <select className="field"
        value={SUPPORTED_LANGUAGES.some((l) => l.code === current) ? current : 'en'}
        onChange={(e) => { void i18n.changeLanguage(e.target.value); }}
        aria-label="Language"
      >
        {SUPPORTED_LANGUAGES.map((l) => (
          <option key={l.code} value={l.code}>{l.flag} {l.displayCode}</option>
        ))}
      </select>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
// WP-16 (login/registro) — Piezas compartidas de presentación (look&feel del demo,
// clases .acc-* tokenizadas). No hay lógica de auth aquí; solo cromo reutilizable.
// ─────────────────────────────────────────────────────────────────────────────

// Logo Fundamentales por idioma. Assets presentes: es/en/fr/pt/ro. `de` y `nl` aún NO tienen
// asset propio → fallback a inglés (pendiente Mau .AI). Cualquier idioma no mapeado → español.
const LOGO_BY_LANG: Record<string, string> = {
  es: asset('/assets/logo-fundamentales-es.svg'),
  en: asset('/assets/logo-fundamentales-en.svg'),
  fr: asset('/assets/logo-fundamentales-fr.svg'),
  pt: asset('/assets/logo-fundamentales-pt.svg'),
  ro: asset('/assets/logo-fundamentales-ro.svg'),
  de: asset('/assets/logo-fundamentales-en.svg'),
  nl: asset('/assets/logo-fundamentales-en.svg'),
};

/** Logo de marca centrado, cambia por idioma (CPE-012). */
export function AuthLogo() {
  const { i18n } = useTranslation('recognition');
  const lang = i18n.language?.split('-')[0] ?? 'es';
  const src = LOGO_BY_LANG[lang] ?? LOGO_BY_LANG.es;
  return (
    <div className="acc-logo">
      <img className="acc-logo-img" src={src} alt="Fundamentales" />
    </div>
  );
}

interface EntraBlockProps {
  /** Título/subtítulo: el mismo bloque se usa en /login (puerta de entrada) y en /app/registro,
   *  con copy distinto en cada pantalla — la acción (`onEnter`) es idéntica en las dos. */
  title: string;
  subtitle: string;
  onEnter: () => void;
}

/**
 * Propuesta de reestructura del login (pendiente de visto bueno del cliente) — bloque de acceso con
 * cuenta Sigma (Entra ID), reutilizado en /login y en /app/registro para no duplicar el marcado.
 *
 * Sin lógica de MSAL aquí a propósito (ver la nota del archivo: "no hay lógica de auth, solo cromo
 * reutilizable") — cada pantalla pasa su propio `onEnter` con el `loginRedirect` real.
 */
export function EntraBlock({ title, subtitle, onEnter }: Readonly<EntraBlockProps>) {
  const { t } = useTranslation('recognition');
  return (
    <div className="acc-route">
      <h2 className="acc-route-title">{title}</h2>
      <p className="acc-route-sub">{subtitle}</p>
      <button type="button" className="acc-entra" onClick={onEnter}>
        <i className="fab fa-microsoft" aria-hidden="true" /> {t('login.entra', 'Entrar con cuenta Sigma')}
      </button>
    </div>
  );
}

interface AuthFieldProps {
  /** Clase FontAwesome del icono a la izquierda, p.ej. 'fa-envelope' / 'fa-lock'. */
  icon: string;
  type?: string;
  /** Muestra el botón ojo y alterna entre password/text. */
  withEye?: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  autoComplete?: string;
  name?: string;
  /**
   * Se dispara al salir del campo. Existe para validar el formato **al cambiar de foco** en vez de
   * esperar al envío: enterarte de que el correo está mal cuando ya rellenaste nómina, contraseña y
   * aceptaste el aviso legal es tarde, y encima el aviso llega en un modal que tapa el campo que hay
   * que corregir.
   */
  onBlur?: () => void;
}

/** Campo de texto con icono a la izquierda y, opcionalmente, botón ojo (show/hide) — CPE-004. */
export function AuthField({
  icon, type = 'text', withEye = false, value, onChange, placeholder, ariaLabel, autoComplete, name,
  onBlur,
}: Readonly<AuthFieldProps>) {
  const { t } = useTranslation('recognition');
  const [show, setShow] = useState(false);
  const inputType = withEye ? (show ? 'text' : 'password') : type;
  return (
    <div className="acc-field acc-field--icon">
      <i className={`acc-field-icon fas ${icon}`} aria-hidden="true" />
      <input
        type={inputType}
        className={withEye ? 'acc-field--haseye' : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        autoComplete={autoComplete}
        name={name}
      />
      {withEye && (
        <button
          type="button"
          className="acc-eye"
          onClick={() => setShow((s) => !s)}
          aria-label={t('login.password', 'Contraseña')}
          aria-pressed={show}
        >
          <i className={`fas ${show ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/** Leyenda de ayuda "Capital Humano" (regla #1) — usable en login y registro. */
export function AuthHelp() {
  const { t } = useTranslation('recognition');
  return (
    <p className="acc-help">
      {t('auth.contactHR', 'Si tienes problemas para registrarte, contacta a tu equipo de Capital Humano.')}
    </p>
  );
}

/**
 * Modal de política del REGISTRO. Es el mismo cuerpo que el modal bloqueante del primer acceso
 * (`LegalPolicyModal`); aquí sólo se fijan las dos diferencias de esta pantalla: **se puede cerrar**
 * —lo abre un enlace, la lectura es opcional— y **no lleva insignia de versión**, porque no es una
 * re-aceptación.
 *
 * Lo demás —visor de PDF, la clase de scroll sin `overflow`, la espera a `onLoaded`, el tamaño con
 * PDF, el portal, el aviso con «Reintentar» y el placeholder— vive en el cuerpo común. Si hay que
 * arreglar algo de eso, se arregla ahí y los dos modales lo reciben.
 */
export function RegisterPolicyModal({
  documentKey, onAccept, onClose, ouId,
}: Readonly<{
  documentKey: LegalDocType;
  /** Se invoca cuando el usuario acepta (tras llegar al final — CPE-009). */
  onAccept: () => void;
  onClose: () => void;
  /** OU elegida en el registro: decide qué PDF corporativo alcanza a esta persona. */
  ouId?: string;
}>) {
  return (
    <LegalPolicyModal
      documentKey={documentKey}
      ouId={ouId}
      dismissible
      onClose={onClose}
      onAccept={() => onAccept()}
    />
  );
}

/**
 * Cuenta atrás para los botones que mandan correo. Devuelve los segundos que faltan (0 = disponible)
 * y la función que la arranca.
 *
 * 🔴 Es un antirrebote VISUAL, no una defensa: quien quiera saltárselo recarga la página. El límite
 * de verdad lo aplica el servidor (1 correo por minuto y 5 por día, por cuenta y por IP). Esto está
 * para que la persona sepa que su clic sirvió de algo y no lo repita creyendo que no pasó nada —
 * porque la respuesta es un 202 opaco y no puede decirle si el correo salió.
 */
export function useCooldown(seconds: number): readonly [number, () => void] {
  const [restante, setRestante] = useState(0);

  useEffect(() => {
    if (restante <= 0) return;
    const id = window.setTimeout(() => setRestante((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [restante]);

  return [restante, useCallback(() => setRestante(seconds), [seconds])] as const;
}

interface SolicitudPorCorreoProps {
  /** Texto del enlace que abre el formulario. */
  etiquetaEnlace: string;
  /** Frase corta bajo el enlace, ya abierto. */
  descripcion: string;
  etiquetaBoton: string;
  /** El MISMO mensaje para todos los casos: es lo que impide usar esto como buscador de correos. */
  mensajeOpaco: string;
  /** Correo con el que arranca el campo (p.ej. el que la persona ya tecleó en el login). */
  correoInicial?: string;
  onEnviar: (correo: string) => void;
  enviando?: boolean;
}

/**
 * Enlace que abre un formulario de una sola línea —el correo— y, al enviarlo, muestra SIEMPRE el
 * mismo acuse.
 *
 * 🔴 <b>Ese «siempre» es el requisito, no un descuido de la UI.</b> Lo usan «¿olvidaste tu
 * contraseña?» y «no recibí el correo de verificación», los dos anónimos: si el mensaje cambiara
 * según si la cuenta existe, cualquiera podría averiguar qué correos están dados de alta en Sigma
 * tecleándolos aquí. El backend responde 202 invariable por lo mismo, así que esta pantalla no
 * tendría forma de distinguirlos ni queriendo.
 */
export function SolicitudPorCorreo({
  etiquetaEnlace, descripcion, etiquetaBoton, mensajeOpaco, correoInicial = '', onEnviar, enviando,
}: Readonly<SolicitudPorCorreoProps>) {
  const { t } = useTranslation('recognition');
  const [abierto, setAbierto] = useState(false);
  const [correo, setCorreo] = useState(correoInicial);
  const [enviado, setEnviado] = useState(false);
  const [restante, arrancarCooldown] = useCooldown(60);

  // El correo tecleado en el formulario de al lado es casi siempre el que se quiere usar aquí.
  useEffect(() => { if (!abierto) setCorreo(correoInicial); }, [correoInicial, abierto]);

  if (!abierto) {
    return (
      <button type="button" className="acc-forgot" onClick={() => setAbierto(true)}>
        {etiquetaEnlace}
      </button>
    );
  }

  return (
    <div className="acc-forgot-note">
      <p className="acc-forgot-intro">{descripcion}</p>
      <AuthField
        icon="fa-envelope" type="email" value={correo} onChange={setCorreo}
        placeholder={t('login.email', 'Correo')} autoComplete="username"
      />
      <button
        type="button"
        className="acc-submit"
        disabled={enviando || restante > 0 || !correo.trim()}
        onClick={() => { onEnviar(correo.trim()); setEnviado(true); arrancarCooldown(); }}
      >
        {restante > 0
          ? t('auth.retryIn', 'Puedes reintentar en {{seconds}} s', { seconds: restante })
          : etiquetaBoton}
      </button>
      {enviado && <p role="status" className="acc-forgot-ack">{mensajeOpaco}</p>}
    </div>
  );
}

interface AuthErrorModalProps {
  /** Mensaje a mostrar; el modal se oculta solo cuando es `null`. */
  message: string | null;
  onClose: () => void;
}

/**
 * Modal de error de login/registro: antes el error de credenciales o de validación del formulario
 * se mostraba como texto debajo del botón de enviar y pasaba desapercibido (se confundía con el
 * resto del formulario). Reutiliza el `Modal` genérico de `components/common` para que el error
 * quede al frente, en vez de duplicar un overlay propio de esta zona.
 */
export function AuthErrorModal({ message, onClose }: Readonly<AuthErrorModalProps>) {
  const { t } = useTranslation('recognition');
  return (
    <Modal open={message !== null} onClose={onClose} maxWidth={420} labelledBy="auth-error-modal-title">
      <ModalHeader onClose={onClose}>
        <span id="auth-error-modal-title">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t('auth.errorTitle', 'No pudimos continuar')}
        </span>
      </ModalHeader>
      <ModalBody>
        <p role="alert">{message}</p>
      </ModalBody>
      <ModalFooter>
        <button type="button" className="acc-submit" onClick={onClose}>{t('common.close', 'Cerrar')}</button>
      </ModalFooter>
    </Modal>
  );
}
